import { useCallback, useState } from 'react';
import { ContentKeywordGq } from '../../../../../../graphql/projects/models/ContentKeywordGq';
import { ContentKeywordSearchTableState } from './useContentKeywordSearchTableState';

export interface SelectState {
  dragActive: boolean;
  isSelecting: boolean;
  dragStartIndex: number;
  dragEndIndex: number;
}

export type ContentKeywordSearchSelectBag = ReturnType<typeof useContentKeywordSearchSelectBag>;

export function useContentKeywordSearchSelectBag(
  tableState: ContentKeywordSearchTableState,
  contentKeywords: ContentKeywordGq[]
) {
  // Initiate state
  const [dragState, setDragState] = useState<SelectState>({
    dragActive: false,
    isSelecting: false,
    dragStartIndex: 0,
    dragEndIndex: 0,
  });

  // Activate drag
  const onMouseDownKeyword = useCallback(
    (contentKeywordId: number, keywordIndex: number) => {
      // Don't double activate
      if (dragState.dragActive) {
        return;
      }

      // Figure out if we're "selecting" or "deselecting"
      // If keyword is already selected, it should become a "deselecting" action
      const selecting = !tableState.selectionState.contentKeywordIds[contentKeywordId];

      setDragState({
        dragActive: true,
        isSelecting: selecting,
        dragStartIndex: keywordIndex,
        dragEndIndex: keywordIndex,
      });
    },
    [dragState.dragActive]
  );

  // Hover a row
  const onMouseOverKeyword = useCallback(
    (keywordIndex: number) => {
      // Do nothing if not active
      if (!dragState.dragActive) return;

      // Change end index
      setDragState((prev) => ({ ...prev, dragEndIndex: keywordIndex }));
    },
    [dragState.dragActive]
  );

  // Deactivating drag
  const minKeywordIndex = Math.min(dragState.dragStartIndex, dragState.dragEndIndex);
  const maxKeywordIndex = Math.max(dragState.dragStartIndex, dragState.dragEndIndex);
  const deactivateDrag = useCallback(() => {
    setDragState({
      isSelecting: false,
      dragActive: false,
      dragStartIndex: 0,
      dragEndIndex: 0,
    });

    // Select keywords
    const contentKeywordIds = contentKeywords
      .slice(minKeywordIndex, maxKeywordIndex + 1)
      .map((cw) => cw.id);
    tableState.handleSelectMany(contentKeywordIds, dragState.isSelecting);
  }, [contentKeywords, dragState]);

  // Function for figuring out if row is selected
  const isIndexSelected = (index: number) =>
    dragState.dragActive && index >= minKeywordIndex && index <= maxKeywordIndex;

  return {
    // We only provide event functions if appropriate state is active/inactive
    // This is to prevent binding lots of functions to the DOM when they're not relevant
    // ... Given that the keyboard row components can have 100 rows and should (1) avoid re-rendering and (2) avoid binding event handlers on every row unless necessary
    deactivateDragMode: dragState.dragActive ? deactivateDrag : undefined,
    onMouseOver: dragState.dragActive ? onMouseOverKeyword : undefined,
    onMouseDown: !dragState.dragActive ? onMouseDownKeyword : undefined,
    isIndexSelected,
  };
}
